import React, { useEffect, useRef, useState, useContext } from "react";
import mapboxgl from "mapbox-gl";
import 'mapbox-gl/dist/mapbox-gl.css';
import { TrafficContext } from "../App";
import { Tooltip } from 'react-tooltip';
import Aerodromes from "./mapelements/Aerodromes";
import Traffic from "./mapelements/Traffic";
import Supplements from "./mapelements/Supplements";
import ATC from "./mapelements/ATC";
import Radar from "./mapelements/Radar";
import Wind from "./mapelements/Wind";
import Lara from "./mapelements/Lara";
import FIX from "./mapelements/FIX";
import VFR from "./mapelements/VFR";
import VOR from "./mapelements/VOR";
import SIGMET from "./mapelements/SIGMET";
import AMA from "./mapelements/AMA";
import Centerlines from "./mapelements/Centerlines";
import resetIcon from '../images/reset.svg';

const debounce = (func, wait) => {
  let timeout;
  return (...args) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func.apply(this, args), wait);
  };
};

mapboxgl.accessToken = "pk.eyJ1Ijoib3R0b3R1aGt1bmVuIiwiYSI6ImNtc3lrcGRjeDAwMWIzMHNmaGRiMmlkMHYifQ.XdO83W6g3pyTXtsTHhYcBw";

const MapComponent = ({ openWindows, setOpenWindows, setWindowSize }) => {
  const mapContainer = useRef(null);
  const mapRef = useRef(null);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [componentsReady, setComponentsReady] = useState(false); // New state
  const traffic = useContext(TrafficContext);
  const [map, setMap] = useState(null);
  const [routeExists, setRouteExists] = useState(false);

  useEffect(() => {
    if (!mapContainer.current) return;

    const mapInstance = new mapboxgl.Map({
      container: mapContainer.current,
      style: "mapbox://styles/ottotuhkunen/cm8hnlorw012z01qrfr8t4nz3",
      center: [24, 64.8],
      zoom: 4.2,
      antialias: true,
      pitchWithRotate: false,
      maxPitch: 0
    });

    mapRef.current = mapInstance;

    mapInstance.on('load', () => {
      setMapLoaded(true);
      setMap(mapInstance);
      updateEFJYVisibility(mapInstance);
      
      // Add 500ms delay to ensure map is fully ready
      setTimeout(() => {
        setComponentsReady(true);
      }, 1000);
    });

    mapInstance.on('error', (e) => {
      console.error('Map error:', e.error);
    });

    return () => {
      if (mapRef.current) {
        mapRef.current.remove();
      }
    };
  }, []);

  const handleResize = debounce(() => {
    if (mapRef.current) {
      mapRef.current.resize(); // Resizes the map
    }
  }, 200); // Debounce with a 200ms delay

  useEffect(() => {
    // Initialize ResizeObserver to listen for container size changes
    const resizeObserver = new ResizeObserver(handleResize);
    resizeObserver.observe(mapContainer.current);

    // Cleanup
    return () => {
      resizeObserver.disconnect(); // Disconnect observer on component unmount
    };
  }, []);

  // Function to check if EFJY CTA should be visible
  const isEFJYVisible = () => {
    const now = new Date();
    const utcHour = now.getUTCHours();
    const utcDay = now.getUTCDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday

    // Determine summer time based on approximate European DST (last Sunday of March to last Sunday of October)
    const isSummerTime = (now.getMonth() > 2 && now.getMonth() < 9) || 
      (now.getMonth() === 2 && now.getUTCDate() >= (31 - (new Date(now.getUTCFullYear(), 2, 31).getDay()))) || 
      (now.getMonth() === 9 && now.getUTCDate() < (31 - (new Date(now.getUTCFullYear(), 9, 31).getDay())));

    const timeOffset = isSummerTime ? 1 : 0; // Summer time starts at UTC+3, otherwise UTC+2

    // Define allowed hours based on UTC
    const monThuStart = 7 - timeOffset;
    const monThuEnd = 14 - timeOffset;
    const friStart = 7 - timeOffset;
    const friEnd = 12 - timeOffset;

    if (utcDay >= 1 && utcDay <= 4) { // Monday to Thursday
      return utcHour >= monThuStart && utcHour < monThuEnd;
    } else if (utcDay === 5) { // Friday
      return utcHour >= friStart && utcHour < friEnd;
    }

    return false; // Hidden on weekends and outside time ranges
  };

  // Function to update the visibility of EFJY CTA
  const updateEFJYVisibility = (mapInstance) => {
    if (!mapInstance.getLayer("lower-ats-airspace-c9rdjr")) return;

    const filter = isEFJYVisible()
      ? ["all"] // Show everything
      : ["all", ["!=", ["get", "name"], "EFJY CTA"]]; // Hide EFJY CTA

    mapInstance.setFilter("lower-ats-airspace-c9rdjr", filter);
  };

  // Periodic update every 5 minutes
  useEffect(() => {
    if (!mapRef.current) return;

    const interval = setInterval(() => {
      updateEFJYVisibility(mapRef.current);
    }, 5 * 60 * 1000); // Every 5 minutes

    return () => clearInterval(interval);
  }, [mapLoaded]);

  const removeRouteFromMap = (map) => {
    if (!map || !map.getSource("route")) return;
  
    map.removeLayer("route-line");
    map.removeLayer("route-points");
    map.removeSource("route");

    setRouteExists(false); 
  };  

  return (
    <div className="h-full w-full">
      {!componentsReady && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-800 bg-opacity-50 z-50">
          <div className="text-white">Loading map...</div>
        </div>
      )}

      <div className="absolute top-0 pt-[34px] right-1 z-10">
        <button
          data-tooltip-id="map-reset-tooltip"
          data-tooltip-content="Reset View"
          className="w-8 h-8 bg-gray-400 text-white flex items-center justify-center"
          onClick={() => {
            if (mapRef.current) {
              mapRef.current.flyTo({
                center: [24, 64.8],
                zoom: 4.2,
                pitch: 0,
                bearing: 0,
                essential: true
              });
            }
          }}
        >
          <img className="w-[18px]" src={resetIcon} alt="Reset" />
        </button>
      </div>
      
      <div className="absolute left-0 top-0 w-10 h-full flex flex-col p-1 space-y-1 z-10 pt-[34px]">
        {componentsReady && (
          <>
            <Centerlines map={mapRef.current} />
            <Aerodromes map={mapRef.current} openWindows={openWindows} setOpenWindows={setOpenWindows} setWindowSize={setWindowSize} />
            <Traffic map={mapRef.current} traffic={traffic} openWindows={openWindows} setOpenWindows={setOpenWindows} setWindowSize={setWindowSize} setRouteExists={setRouteExists} />
            <ATC map={mapRef.current} />
            <Supplements map={mapRef.current} openWindows={openWindows} setOpenWindows={setOpenWindows} setWindowSize={setWindowSize}/>
            <Lara map={mapRef.current} openWindows={openWindows} setOpenWindows={setOpenWindows} setWindowSize={setWindowSize} />
            <FIX map={mapRef.current} />
            <VFR map={mapRef.current} />
            <VOR map={mapRef.current} />
            <AMA map={mapRef.current} />
            <Radar map={mapRef.current} />
            <Wind map={mapRef.current} />
            <SIGMET map={mapRef.current} openWindows={openWindows} setOpenWindows={setOpenWindows} setWindowSize={setWindowSize}/>

          </>
        )}
      </div>

      {componentsReady && routeExists && (
        <div className="absolute top-[40px] left-1/2 transform -translate-x-1/2 text-sm z-10 bg-red-500 p-1">
          <a
            href="#"
            onClick={(e) => {
              e.preventDefault();
              removeRouteFromMap(mapRef.current);
            }}
            style={{ cursor: "pointer", color: "white" }}
          >
            Remove Route
          </a>
        </div>
      )}

      <div ref={mapContainer} className="h-full w-full z-0" />
      
      <Tooltip 
        id="map-buttons-tooltip" 
        place="right" 
        effect="solid" 
        className="z-50" 
        data-tooltip-place="right"
        style={{ 
          backgroundColor: "#4B5563", 
          padding: '2px 6px', 
          borderRadius: '0',
          zIndex: 1000
        }}
      />
      <Tooltip 
        id="map-reset-tooltip" 
        place="left" 
        effect="solid" 
        className="z-50" 
        data-tooltip-place="right"
        style={{ 
          backgroundColor: "#4B5563", 
          padding: '2px 6px', 
          borderRadius: '0',
          zIndex: 1000
        }}
      />
    </div>
  );
};

export default MapComponent;
